import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { alert, haptic } from '../lib/telegram';
import type { UserSettings } from '../lib/types';
import type { Screen } from '../App';

interface Props {
  onBack: () => void;
  navigate: (s: Screen) => void;
}

// Browser's offset is in MINUTES BEHIND UTC. Flip the sign so positive = ahead of UTC.
const browserTzOffsetMinutes = -new Date().getTimezoneOffset();

export default function Settings({ onBack, navigate }: Props) {
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getSettings()
      .then(setSettings)
      .catch((e) => setErr(e instanceof Error ? e.message : 'failed to load'))
      .finally(() => setLoading(false));
  }, []);

  async function patch(p: Partial<UserSettings>) {
    setSaving(true); setErr(null); setSavedMsg(null);
    try {
      const updated = await api.updateSettings({
        reminder_enabled: p.reminder_enabled,
        reminder_time_local: p.reminder_time_local,
        tz_offset_minutes: p.tz_offset_minutes,
      });
      setSettings(updated);
      haptic('success');
      setSavedMsg('Saved');
      setTimeout(() => setSavedMsg(null), 1500);
    } catch (e) {
      haptic('error');
      setErr(e instanceof Error ? e.message : 'failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function sendTestReminder() {
    try {
      await api.sendTestReminder();
      haptic('success');
      alert('Reminder sent! Check your bot chat — tap a snooze button to test.');
    } catch (e) {
      haptic('error');
      alert(e instanceof Error ? e.message : 'failed to send');
    }
  }

  async function sendTestSummary() {
    try {
      await api.notifyToday();
      haptic('success');
      alert('Summary sent! Check your bot chat.');
    } catch (e) {
      haptic('error');
      alert(e instanceof Error ? e.message : 'failed to send');
    }
  }

  async function exportData() {
    try {
      const r = await api.exportCsv();
      haptic('success');
      alert(`Exported ${r.count} entries — check your bot chat for the .csv file.`);
    } catch (e) {
      haptic('error');
      alert(e instanceof Error ? e.message : 'export failed');
    }
  }

  if (loading) return (
    <>
      <div className="header"><button className="back" onClick={onBack}>← Back</button><h1>Settings</h1></div>
      <div className="card"><div className="muted">Loading…</div></div>
    </>
  );

  const s = settings;
  const time = s?.reminder_time_local || '21:00';
  const tzLooksWrong = s && s.tz_offset_minutes !== browserTzOffsetMinutes;

  return (
    <>
      <div className="header">
        <button className="back" onClick={onBack}>← Back</button>
        <h1>Settings</h1>
      </div>
      {err && <div className="error">{err}</div>}

      <div className="card">
        <div className="muted small" style={{ marginBottom: 8 }}>Daily reminder</div>

        <label className="row" style={{ borderTop: 'none', padding: '8px 0' }}>
          <span>Remind me to log spending</span>
          <input
            type="checkbox"
            checked={!!s?.reminder_enabled}
            disabled={saving}
            onChange={(e) => patch({ reminder_enabled: e.target.checked, tz_offset_minutes: browserTzOffsetMinutes, reminder_time_local: s?.reminder_time_local || time })}
            style={{ width: 22, height: 22 }}
          />
        </label>

        <label className="label">Reminder time (your local time)</label>
        <input
          className="input"
          type="time"
          value={time}
          disabled={saving}
          onChange={(e) => patch({ reminder_time_local: e.target.value, tz_offset_minutes: browserTzOffsetMinutes, reminder_enabled: !!s?.reminder_enabled })}
        />

        <div className="muted small" style={{ marginTop: 10 }}>
          Timezone: UTC{browserTzOffsetMinutes >= 0 ? '+' : ''}{(browserTzOffsetMinutes / 60).toFixed(1).replace(/\.0$/, '')}
          {tzLooksWrong && (
            <button
              className="back"
              style={{ marginLeft: 8 }}
              onClick={() => patch({ tz_offset_minutes: browserTzOffsetMinutes })}
            >
              update saved tz
            </button>
          )}
        </div>

        {s?.snoozed_until && new Date(s.snoozed_until) > new Date() && (
          <div className="muted small" style={{ marginTop: 8 }}>
            Currently snoozed until {new Date(s.snoozed_until).toLocaleString()}
          </div>
        )}

        {savedMsg && <div className="muted small" style={{ marginTop: 8, color: '#2e8b57' }}>{savedMsg}</div>}
      </div>

      <div className="card">
        <div className="muted small" style={{ marginBottom: 8 }}>Money tools</div>
        <button className="btn btn-secondary" onClick={() => navigate({ name: 'cash-counter', from: 'settings' })}>🧮 Cash counter</button>
        <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={() => navigate({ name: 'budgets' })}>🎯 Budgets</button>
        <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={() => navigate({ name: 'debts' })}>🤝 Debts</button>
        <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={() => navigate({ name: 'goals' })}>🏆 Savings goals</button>
      </div>

      <div className="card">
        <div className="muted small" style={{ marginBottom: 8 }}>Customize</div>
        <button className="btn btn-secondary" onClick={() => navigate({ name: 'categories' })}>🏷️ Categories</button>
        <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={() => navigate({ name: 'presets' })}>⚡ Quick presets</button>
        <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={() => navigate({ name: 'recurring' })}>🔁 Recurring entries</button>
      </div>

      <div className="card">
        <div className="muted small" style={{ marginBottom: 8 }}>Test it</div>
        <button className="btn" onClick={sendTestReminder}>🔔 Send test reminder</button>
        <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={sendTestSummary}>📊 Send today's summary</button>
        <p className="muted small" style={{ marginTop: 8, marginBottom: 0 }}>
          If you've never started a chat with the bot, send <code>/start</code> in the bot chat first — Telegram blocks bots from initiating conversations.
        </p>
      </div>

      <div className="card">
        <div className="muted small" style={{ marginBottom: 8 }}>Backup</div>
        <button className="btn btn-secondary" onClick={exportData}>📥 Export all data (CSV)</button>
        <p className="muted small" style={{ marginTop: 8, marginBottom: 0 }}>
          The bot sends a CSV of every entry to your chat — keep it as a backup.
        </p>
      </div>
    </>
  );
}
